import React, { useEffect, useRef, useState } from "react";

import { useCreatorStreams } from "../../../creators/hooks/useCreatorStreams";

import CreatorEditStreamForm from "../forms/CreatorEditStreamForm";

import "./CreatorStreamSetupForm.css";

const getErrorMessage = (error, fallback = "Something went wrong.") => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return error?.response?.data?.message || error?.message || fallback;
};

const formatLabel = (value) => {
  if (!value) {
    return "N/A";
  }

  return String(value).replaceAll("_", " ");
};

const formatDateTime = (value) => {
  if (!value) {
    return "Not set";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Invalid date";
  }

  return date.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CreatorStreamSetupForm = ({
  streamSessionId,
  onClose,
  closeModal,
  onDeleted,
}) => {
  const {
    broadcastData,
    loading,
    error,
    getStreamBroadcastData,
    markStreamLive,
    markStreamEnded,
    deleteStream,
    updateStreamDetails,
    linkIvsRecordingToStream,
    resetStreamState,
  } = useCreatorStreams();

  const [activeTab, setActiveTab] = useState("broadcast");
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const [showStreamKey, setShowStreamKey] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);

  const copyTimerRef = useRef(null);

  const closeView = onClose || closeModal;

  useEffect(() => {
    if (!streamSessionId) {
      return;
    }

    getStreamBroadcastData(streamSessionId);

    return () => {
      if (copyTimerRef.current) {
        window.clearTimeout(copyTimerRef.current);
      }

      resetStreamState();
    };
  }, [streamSessionId]);

  const refreshBroadcastData = async () => {
    if (!streamSessionId) {
      return false;
    }

    return getStreamBroadcastData(streamSessionId);
  };

  const handleSaveStreamDetails = async (payload) => {
    if (!streamSessionId || !payload) {
      return false;
    }

    const result = await updateStreamDetails(streamSessionId, payload);

    if (result === false || result?.success === false) {
      return false;
    }

    await refreshBroadcastData();

    setEditingDetails(false);

    return result;
  };

  const copyToClipboard = async (value, fieldName) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);

      setCopiedField(fieldName);

      if (copyTimerRef.current) {
        window.clearTimeout(copyTimerRef.current);
      }

      copyTimerRef.current = window.setTimeout(() => {
        setCopiedField("");
      }, 1500);
    } catch (copyError) {
      console.error("Copy failed:", copyError);

      window.alert("Could not copy. Please copy manually.");
    }
  };

  const handleStartStream = async () => {
    if (!streamSessionId || actionLoading) {
      return;
    }

    try {
      setActionLoading(true);

      const result = await markStreamLive(streamSessionId);

      if (result === false || result?.success === false) {
        window.alert(result?.message || "Failed to start stream.");

        return;
      }

      await refreshBroadcastData();
    } catch (startError) {
      console.error("Error starting stream:", startError);

      window.alert(getErrorMessage(startError, "Failed to start stream."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndStream = async () => {
    if (!streamSessionId || actionLoading) {
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to end this stream?",
    );

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(true);

      const result = await markStreamEnded(streamSessionId);

      if (result === false || result?.success === false) {
        window.alert(result?.message || "Failed to end stream.");

        return;
      }

      await refreshBroadcastData();

      const replayReady = Boolean(
        result?.replayReady || result?.vodHlsUrl || result?.vodMp4Url,
      );

      if (replayReady) {
        window.alert("Stream ended. Replay is ready.");

        return;
      }

      window.alert("Stream ended. Replay may take a moment to become ready.");
    } catch (endError) {
      console.error("Error ending stream:", endError);

      window.alert(getErrorMessage(endError, "Failed to end stream."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleLinkIvsReplay = async () => {
    if (!streamSessionId || actionLoading) {
      return;
    }

    try {
      setActionLoading(true);

      const result = await linkIvsRecordingToStream(streamSessionId);

      if (result === false || result?.success === false) {
        window.alert(result?.message || "Failed to link IVS replay.");

        return;
      }

      await refreshBroadcastData();

      const replayReady = Boolean(
        result?.stream?.vodHlsUrl ||
        result?.stream?.vodMp4Url ||
        result?.recording?.playbackUrl,
      );

      if (replayReady) {
        window.alert("IVS replay linked successfully.");

        return;
      }

      window.alert("IVS recording is not ready yet. Try again shortly.");
    } catch (linkError) {
      console.error("Error linking IVS replay:", linkError);

      window.alert(getErrorMessage(linkError, "Failed to link IVS replay."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteStream = async () => {
    if (!streamSessionId || actionLoading) {
      return;
    }

    const confirmed = window.confirm(
      `Delete "${
        broadcastData?.title || "this stream"
      }"?\n\nThis will permanently delete the stream and its associated files.`,
    );

    if (!confirmed) {
      return;
    }

    const finalConfirmation = window.confirm(
      "Final confirmation: permanently delete this stream?",
    );

    if (!finalConfirmation) {
      return;
    }

    try {
      setActionLoading(true);

      const result = await deleteStream(streamSessionId);

      if (result === false || result?.success === false) {
        window.alert(result?.message || "Failed to delete stream.");

        return;
      }

      await onDeleted?.(result);

      closeView?.();
    } catch (deleteError) {
      console.error("Error deleting stream:", deleteError);

      window.alert(getErrorMessage(deleteError, "Failed to delete stream."));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !broadcastData?.streamSessionId) {
    return (
      <div className="creator-stream-setup-state" role="status">
        Loading broadcast setup...
      </div>
    );
  }

  if (error) {
    return (
      <div className="creator-stream-setup-error" role="alert">
        {getErrorMessage(error)}
      </div>
    );
  }

  if (!broadcastData?.streamSessionId) {
    return (
      <div className="creator-stream-setup-state">No broadcast data found.</div>
    );
  }

  const streamStatus = String(broadcastData.status || "").toLowerCase();

  const isLive = streamStatus === "live";
  const isEnded = streamStatus === "ended";

  const recordingEnabled =
    Number(
      broadcastData.recordingEnabled ?? broadcastData.recording_enabled,
    ) === 1;

  const hasReplay = Boolean(
    broadcastData.vodHlsUrl ||
    broadcastData.vod_hls_url ||
    broadcastData.vodMp4Url ||
    broadcastData.vod_mp4_url,
  );

  const replayUrl = broadcastData.vodHlsUrl || broadcastData.vod_hls_url || "";

  return (
    <div className="creator-stream-setup-view">
      <div className="creator-stream-setup-header">
        <div>
          <p className="creator-stream-setup-eyebrow">Katch Live</p>

          <p>
            Connect OBS or YoloBox, manage the stream status, and control its
            replay.
          </p>
        </div>

        <span
          className={`creator-stream-setup-status creator-stream-setup-status-${streamStatus}`}
        >
          {formatLabel(streamStatus)}
        </span>
      </div>

      <div className="creator-stream-setup-card creator-stream-setup-hero-card">
        <div>
          <h3>{broadcastData.title}</h3>

          {broadcastData.description && (
            <p className="creator-stream-setup-description">
              {broadcastData.description}
            </p>
          )}
        </div>

        <div className="creator-stream-setup-quick-meta">
          <div>
            <span>Stream ID</span>
            <strong>{broadcastData.streamSessionId}</strong>
          </div>

          <div>
            <span>Status</span>
            <strong>{formatLabel(streamStatus)}</strong>
          </div>

          <div>
            <span>Replay</span>
            <strong>{hasReplay ? "Available" : "Not ready"}</strong>
          </div>
        </div>
      </div>

      <div
        className="creator-stream-setup-tabs"
        role="tablist"
        aria-label="Stream setup sections"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "broadcast"}
          className={activeTab === "broadcast" ? "active" : ""}
          onClick={() => setActiveTab("broadcast")}
        >
          Broadcast Setup
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "details"}
          className={activeTab === "details" ? "active" : ""}
          onClick={() => setActiveTab("details")}
        >
          Stream Details
        </button>
      </div>

      {activeTab === "broadcast" && (
        <div className="creator-stream-setup-tab-panel" role="tabpanel">
          <div className="creator-stream-setup-card">
            <h3>OBS / YoloBox Settings</h3>

            <div className="creator-stream-setup-field">
              <label htmlFor="creator-stream-server-url">Server URL</label>

              <div className="creator-stream-setup-copy-row">
                <input
                  id="creator-stream-server-url"
                  type="text"
                  value={broadcastData.obsServerUrl || ""}
                  readOnly
                />

                <button
                  type="button"
                  onClick={() =>
                    copyToClipboard(broadcastData.obsServerUrl, "server")
                  }
                >
                  {copiedField === "server" ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div className="creator-stream-setup-field">
              <label htmlFor="creator-stream-key">Stream Key</label>

              <div className="creator-stream-setup-copy-row">
                <input
                  id="creator-stream-key"
                  type={showStreamKey ? "text" : "password"}
                  value={broadcastData.ivsStreamKeyValue || ""}
                  readOnly
                />

                <button
                  type="button"
                  onClick={() => setShowStreamKey((current) => !current)}
                >
                  {showStreamKey ? "Hide" : "Show"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    copyToClipboard(broadcastData.ivsStreamKeyValue, "key")
                  }
                >
                  {copiedField === "key" ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div className="creator-stream-setup-instructions">
              <p>
                1. In OBS, go to <strong>Settings → Stream</strong>.
              </p>

              <p>
                2. Choose <strong>Custom</strong> as the service.
              </p>

              <p>
                3. Paste the <strong>Server URL</strong> and{" "}
                <strong>Stream Key</strong>.
              </p>

              <p>
                4. Click <strong>Start Streaming</strong> in OBS or YoloBox.
              </p>
            </div>
          </div>

          <div className="creator-stream-setup-card">
            <h3>Broadcast Controls</h3>

            <div className="creator-stream-setup-actions">
              <button
                type="button"
                className="creator-stream-setup-start-btn"
                disabled={actionLoading || isLive || isEnded}
                onClick={handleStartStream}
              >
                {actionLoading ? "Please wait..." : "Mark Live"}
              </button>

              <button
                type="button"
                className="creator-stream-setup-end-btn"
                disabled={actionLoading || !isLive || isEnded}
                onClick={handleEndStream}
              >
                End Stream
              </button>

              {isEnded && !hasReplay && recordingEnabled && (
                <button
                  type="button"
                  className="creator-stream-setup-link-btn"
                  disabled={actionLoading}
                  onClick={handleLinkIvsReplay}
                >
                  {actionLoading ? "Linking..." : "Link IVS Replay"}
                </button>
              )}
            </div>

            <p className="creator-stream-setup-help-text">
              {!isEnded
                ? "Stop streaming from OBS or YoloBox before clicking End Stream."
                : hasReplay
                  ? "The IVS replay is linked and ready."
                  : "The stream has ended. Link the replay after IVS finishes preparing the recording."}
            </p>
          </div>
        </div>
      )}

      {activeTab === "details" && (
        <div className="creator-stream-setup-tab-panel" role="tabpanel">
          <div className="creator-stream-setup-grid">
            <div className="creator-stream-setup-card">
              <div className="creator-stream-setup-section-header">
                <h3>Stream Details</h3>

                <button
                  type="button"
                  className="creator-stream-setup-edit-btn"
                  onClick={() => setEditingDetails((current) => !current)}
                  disabled={actionLoading}
                >
                  {editingDetails ? "Close Edit" : "Edit Details"}
                </button>
              </div>

              {editingDetails ? (
                <CreatorEditStreamForm
                  stream={broadcastData}
                  loading={loading}
                  error={error}
                  onSave={handleSaveStreamDetails}
                  onClose={() => setEditingDetails(false)}
                />
              ) : (
                <div className="creator-stream-setup-detail-grid">
                  <div>
                    <span>Stream Type</span>
                    <strong>
                      {formatLabel(
                        broadcastData.streamType || broadcastData.stream_type,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Access</span>
                    <strong>
                      {formatLabel(
                        broadcastData.accessType || broadcastData.access_type,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Scheduled</span>
                    <strong>
                      {formatDateTime(
                        broadcastData.scheduledStart ||
                          broadcastData.scheduled_start,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Recording</span>
                    <strong>{recordingEnabled ? "Enabled" : "Off"}</strong>
                  </div>

                  <div>
                    <span>Started</span>
                    <strong>
                      {formatDateTime(
                        broadcastData.actualStart || broadcastData.actual_start,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Ended</span>
                    <strong>
                      {formatDateTime(
                        broadcastData.actualEnd || broadcastData.actual_end,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Replay Status</span>
                    <strong>
                      {formatLabel(
                        broadcastData.mediaConvertStatus ||
                          broadcastData.media_convert_status,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Channel</span>
                    <strong>
                      {broadcastData.ivsChannelName ||
                        broadcastData.ivs_channel_name ||
                        "N/A"}
                    </strong>
                  </div>
                </div>
              )}
            </div>

            <div className="creator-stream-setup-card">
              <h3>Replay Details</h3>

              <div className="creator-stream-setup-replay-box">
                <div>
                  <span>Replay URL</span>

                  <strong>{hasReplay ? "Ready" : "Not available yet"}</strong>
                </div>

                {replayUrl && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(replayUrl, "replay")}
                  >
                    {copiedField === "replay" ? "Copied" : "Copy URL"}
                  </button>
                )}
              </div>

              <p className="creator-stream-setup-help-text">
                Replay becomes available after the stream ends and its IVS
                recording is linked.
              </p>
            </div>
          </div>

          <div className="creator-stream-setup-card creator-stream-setup-danger-zone">
            <h3>Danger Zone</h3>

            <p>
              Permanently delete this stream and its associated records and
              files. This cannot be undone.
            </p>

            <div className="creator-stream-setup-actions">
              <button
                type="button"
                className="creator-stream-setup-delete-btn"
                disabled={actionLoading}
                onClick={handleDeleteStream}
              >
                {actionLoading ? "Please wait..." : "Delete Stream"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorStreamSetupForm;
